import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Card,
  Form,
  Table,
  Spinner,
  Alert,
  Button,
  Modal,
} from "react-bootstrap";

import {
  Search,
  X,
  Plus,
  Save,
  Pencil,
  Download,
  Trash2,
  AlertTriangle,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import NavbarUtility from "./NavbarUtility";
import * as XLSX from "xlsx";

// ==========================================================
// TOKEN DI SISTEMA
// ==========================================================

const GOLD = "#ebb51b";
const GOLD_DARK = "#a67a10";
const BG_PAGE = "#121419";
const PANEL_BG = "rgba(43, 47, 56, 0.78)";
const PANEL_BORDER = "rgba(255,255,255,0.10)";
const THEAD_BG = "rgba(255,255,255,0.065)";
const THEAD_TEXT = "#e6bd4d";
const TEXT_DARK = "#f1f1ee";
const TEXT_MUTED = "#a9aaad";
const TEXT_ON_PAGE = "#f1f1ee";

const DELETE_CONTACT_API =
  "https://dashboard.technoparts.it/ws/add_contatti.php";

// ==========================================================
// COMPONENTE
// ==========================================================

const Contatti = () => {
  const navigate = useNavigate();

  // ========================================================
  // STATE
  // ========================================================

  const [contatti, setContatti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState("");

  // Eliminazione contatto
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [contattoDaEliminare, setContattoDaEliminare] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Filtri
  const [ricerca, setRicerca] = useState("");
  const [fiera, setFiera] = useState("");
  const [nazione, setNazione] = useState("");

  const [soloClienti, setSoloClienti] = useState(false);
  const [soloFornitori, setSoloFornitori] = useState(false);
  const [soloMeccanica, setSoloMeccanica] = useState(false);
  const [soloCarrozzeria, setSoloCarrozzeria] = useState(false);
  const [soloVetro, setSoloVetro] = useState(false);

  // Ordinamento tabella.
  // Un click ordina crescente, il click successivo sulla stessa colonna inverte.
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  // ========================================================
  // CARICAMENTO CONTATTI
  // ========================================================

  useEffect(() => {
    const caricaContatti = async () => {
      try {
        setLoading(true);
        setErrore("");

        const response = await fetch(
          "https://dashboard.technoparts.it/ws/contatti.php",
          { cache: "no-store" },
        );

        if (!response.ok) {
          throw new Error(`Errore HTTP ${response.status}`);
        }

        const data = await response.json();

        console.log("=================================");
        console.log("RISPOSTA API CONTATTI");
        console.log(data);
        console.log("=================================");

        const elenco = Array.isArray(data?.CONTATTI) ? data.CONTATTI : [];

        console.log("Contatti caricati:", elenco.length);

        if (elenco.length > 0) {
          console.log("PRIMO CONTATTO:", elenco[0]);
        }

        setContatti(elenco);
      } catch (error) {
        console.error("Errore caricamento contatti:", error);

        setErrore(
          "Impossibile caricare i contatti. Verifica che il servizio PHP sia raggiungibile.",
        );
      } finally {
        setLoading(false);
      }
    };

    caricaContatti();
  }, []);

  // ========================================================
  // NORMALIZZAZIONE + DEDUPLICAZIONE CONTATTI (backup frontend)
  // ========================================================

  const normalizzaTesto = (valore) =>
    String(valore ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

  const punteggioFiera = (contatto) => {
    const nomeFiera = String(contatto?.fiera ?? "");
    const anni = nomeFiera.match(/(?:19|20)\d{2}/g) || [];
    const anno = anni.length ? Math.max(...anni.map(Number)) : 0;
    const idFiera = Number(contatto?.id_fiera ?? contatto?.id_fiera_ultima ?? 0) || 0;

    return { anno, idFiera };
  };

  const scegliContattoConFieraPiuRecente = (a, b) => {
    if (!a?.fiera) return b?.fiera ? b : a;
    if (!b?.fiera) return a;

    const pa = punteggioFiera(a);
    const pb = punteggioFiera(b);

    if (pb.anno !== pa.anno) return pb.anno > pa.anno ? b : a;
    if (pb.idFiera !== pa.idFiera) return pb.idFiera > pa.idFiera ? b : a;

    return a;
  };

  const contattiUnici = useMemo(() => {
    const mappa = new Map();

    contatti.forEach((contatto) => {
      const chiave =
        contatto.id_contatto != null &&
        String(contatto.id_contatto).trim() !== ""
          ? `id:${String(contatto.id_contatto).trim()}`
          : contatto.codice_cliente != null &&
              String(contatto.codice_cliente).trim() !== ""
            ? `cli:${String(contatto.codice_cliente).trim()}`
            : `nome:${normalizzaTesto(contatto.ragione_sociale)}|piva:${normalizzaTesto(
                contatto.par_iva,
              )}|indirizzo:${normalizzaTesto(contatto.indirizzo)}`;

      if (!mappa.has(chiave)) {
        mappa.set(chiave, { ...contatto, __rowKey: chiave });
        return;
      }

      // Se per errore l'API restituisce lo stesso contatto più volte,
      // manteniamo una sola riga e conserviamo i valori utili.
      const precedente = mappa.get(chiave);
      const conFieraPiuRecente = scegliContattoConFieraPiuRecente(
        precedente,
        contatto,
      );

      mappa.set(chiave, {
        ...precedente,
        ...contatto,
        id_contatto: precedente.id_contatto || contatto.id_contatto,
        codice_cliente: precedente.codice_cliente || contatto.codice_cliente,
        ragione_sociale: precedente.ragione_sociale || contatto.ragione_sociale,
        provincia: precedente.provincia || contatto.provincia,
        localita: precedente.localita || contatto.localita,
        indirizzo: precedente.indirizzo || contatto.indirizzo,
        cap: precedente.cap || contatto.cap,
        par_iva: precedente.par_iva || contatto.par_iva,
        tipo_contatto: precedente.tipo_contatto || contatto.tipo_contatto,
        nazione: precedente.nazione || contatto.nazione,
        fiera: conFieraPiuRecente?.fiera || precedente.fiera || contatto.fiera,
        id_fiera:
          conFieraPiuRecente?.id_fiera ||
          conFieraPiuRecente?.id_fiera_ultima ||
          precedente.id_fiera ||
          contatto.id_fiera,
        is_cliente:
          String(precedente.is_cliente) === "1" || String(contatto.is_cliente) === "1"
            ? "1"
            : precedente.is_cliente ?? contatto.is_cliente,
        is_fornitore:
          String(precedente.is_fornitore) === "1" ||
          String(contatto.is_fornitore) === "1"
            ? "1"
            : precedente.is_fornitore ?? contatto.is_fornitore,
        tratta_meccanica:
          String(precedente.tratta_meccanica) === "1" ||
          String(contatto.tratta_meccanica) === "1"
            ? "1"
            : precedente.tratta_meccanica ?? contatto.tratta_meccanica,
        tratta_carrozzeria:
          String(precedente.tratta_carrozzeria) === "1" ||
          String(contatto.tratta_carrozzeria) === "1"
            ? "1"
            : precedente.tratta_carrozzeria ?? contatto.tratta_carrozzeria,
        tratta_vetro:
          String(precedente.tratta_vetro) === "1" ||
          String(contatto.tratta_vetro) === "1"
            ? "1"
            : precedente.tratta_vetro ?? contatto.tratta_vetro,
        note_da_fare: Math.max(
          Number(precedente.note_da_fare || 0),
          Number(contatto.note_da_fare || 0),
        ),
        __rowKey: chiave,
      });
    });

    return Array.from(mappa.values());
  }, [contatti]);

  // ========================================================
  // ELENCO FIERE
  // ========================================================

  const fiere = useMemo(() => {
    const elenco = contattiUnici
      .map((contatto) => contatto.fiera)
      .filter(
        (valore) =>
          valore !== null &&
          valore !== undefined &&
          String(valore).trim() !== "",
      )
      .map((valore) => String(valore).trim());

    return [...new Set(elenco)].sort((a, b) =>
      a.localeCompare(b, "it", { sensitivity: "base" }),
    );
  }, [contattiUnici]);

  // ========================================================
  // ELENCO NAZIONI
  // ========================================================

  const nazioni = useMemo(() => {
    const elenco = contattiUnici
      .map((contatto) => contatto.nazione)
      .filter(
        (valore) =>
          valore !== null &&
          valore !== undefined &&
          String(valore).trim() !== "",
      )
      .map((valore) => String(valore).trim());

    return [...new Set(elenco)].sort((a, b) =>
      a.localeCompare(b, "it", { sensitivity: "base" }),
    );
  }, [contattiUnici]);

  // ========================================================
  // ORDINAMENTO TABELLA
  // ========================================================

  const collator = useMemo(
    () =>
      new Intl.Collator("it", {
        sensitivity: "base",
        numeric: true,
        ignorePunctuation: true,
      }),
    [],
  );

  const numericSortKeys = useMemo(
    () =>
      new Set([
        "codice_cliente",
        "cap",
        "par_iva",
        "is_cliente",
        "is_fornitore",
        "tratta_meccanica",
        "tratta_carrozzeria",
        "tratta_vetro",
        "note_da_fare",
      ]),
    [],
  );

  const getSortValue = (contatto, key) => {
    switch (key) {
      case "codice_cliente":
        return contatto.codice_cliente ?? contatto.id_cliente ?? "";
      case "ragione_sociale":
        return contatto.ragione_sociale ?? "";
      case "provincia":
        return contatto.provincia ?? "";
      case "localita":
        return contatto.localita ?? "";
      case "indirizzo":
        return contatto.indirizzo ?? "";
      case "cap":
        return contatto.cap ?? "";
      case "par_iva":
        return contatto.par_iva ?? "";
      case "tipo_contatto":
        return contatto.tipo_contatto ?? "";
      case "nazione":
        return contatto.nazione ?? "";
      case "fiera":
        return contatto.fiera ?? "";
      case "is_cliente":
        return String(contatto.is_cliente) === "1" ? 1 : 0;
      case "is_fornitore":
        return String(contatto.is_fornitore) === "1" ? 1 : 0;
      case "tratta_meccanica":
        return String(contatto.tratta_meccanica) === "1" ? 1 : 0;
      case "tratta_carrozzeria":
        return String(contatto.tratta_carrozzeria) === "1" ? 1 : 0;
      case "tratta_vetro":
        return String(contatto.tratta_vetro) === "1" ? 1 : 0;
      case "note_da_fare":
        return Number(contatto.note_da_fare || 0);
      default:
        return "";
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // ========================================================
  // FILTRAGGIO
  // ========================================================

  const contattiFiltrati = useMemo(() => {
    const testo = normalizzaTesto(ricerca);
    const fieraSelezionata = normalizzaTesto(fiera);
    const nazioneSelezionata = normalizzaTesto(nazione);

    const risultati = contattiUnici.filter((contatto) => {
      // RICERCA: SOLO RAGIONE SOCIALE.
      // Scrivendo "cat ric" può passare CAT RICAMBI, non ORIENTE/AUTOTEAM.
      if (testo) {
        const nome = normalizzaTesto(contatto.ragione_sociale);

        if (!nome.includes(testo)) {
          return false;
        }
      }

      // FIERA: confronto ESATTO. Se è selezionata FRANKFURT 2026,
      // una riga senza fiera non può in alcun modo passare il filtro.
      if (fieraSelezionata) {
        const fieraContatto = normalizzaTesto(contatto.fiera);

        if (!fieraContatto || fieraContatto !== fieraSelezionata) {
          return false;
        }
      }

      // NAZIONE
      if (nazioneSelezionata) {
        const nazioneContatto = normalizzaTesto(contatto.nazione);

        if (!nazioneContatto || nazioneContatto !== nazioneSelezionata) {
          return false;
        }
      }

      if (soloClienti && String(contatto.is_cliente) !== "1") return false;
      if (soloFornitori && String(contatto.is_fornitore) !== "1") return false;
      if (soloMeccanica && String(contatto.tratta_meccanica) !== "1") return false;
      if (soloCarrozzeria && String(contatto.tratta_carrozzeria) !== "1") return false;
      if (soloVetro && String(contatto.tratta_vetro) !== "1") return false;

      return true;
    });

    return [...risultati].sort((a, b) => {
      // Se l'utente ha cliccato una colonna, quella ha la precedenza.
      if (sortConfig.key) {
        const valueA = getSortValue(a, sortConfig.key);
        const valueB = getSortValue(b, sortConfig.key);

        const emptyA =
          valueA === null ||
          valueA === undefined ||
          String(valueA).trim() === "";
        const emptyB =
          valueB === null ||
          valueB === undefined ||
          String(valueB).trim() === "";

        // I valori vuoti rimangono sempre in fondo.
        if (emptyA && !emptyB) return 1;
        if (!emptyA && emptyB) return -1;
        if (emptyA && emptyB) return 0;

        let comparison = 0;

        if (numericSortKeys.has(sortConfig.key)) {
          const numberA = Number(String(valueA).replace(",", "."));
          const numberB = Number(String(valueB).replace(",", "."));

          if (Number.isFinite(numberA) && Number.isFinite(numberB)) {
            comparison = numberA - numberB;
          } else {
            comparison = collator.compare(String(valueA), String(valueB));
          }
        } else {
          comparison = collator.compare(String(valueA), String(valueB));
        }

        return sortConfig.direction === "asc" ? comparison : -comparison;
      }

      // Ordinamento di default già presente: ragione sociale A-Z,
      // dando precedenza ai risultati che iniziano con il testo cercato.
      const nomeA = normalizzaTesto(a.ragione_sociale);
      const nomeB = normalizzaTesto(b.ragione_sociale);

      if (testo) {
        const aInizia = nomeA.startsWith(testo);
        const bInizia = nomeB.startsWith(testo);

        if (aInizia && !bInizia) return -1;
        if (!aInizia && bInizia) return 1;
      }

      return nomeA.localeCompare(nomeB, "it", { sensitivity: "base" });
    });
  }, [
    contattiUnici,
    ricerca,
    fiera,
    nazione,
    soloClienti,
    soloFornitori,
    soloMeccanica,
    soloCarrozzeria,
    soloVetro,
    sortConfig,
    collator,
    numericSortKeys,
  ]);

  // ========================================================
  // RESET FILTRI
  // ========================================================

  const resetFiltri = () => {
    setRicerca("");
    setFiera("");
    setNazione("");
    setSoloClienti(false);
    setSoloFornitori(false);
    setSoloMeccanica(false);
    setSoloCarrozzeria(false);
    setSoloVetro(false);
  };

  const filtriAttivi =
    ricerca ||
    fiera ||
    nazione ||
    soloClienti ||
    soloFornitori ||
    soloMeccanica ||
    soloCarrozzeria ||
    soloVetro;

  // ========================================================
  // // EXPORT EXCEL — SOLO RIGHE FILTRATE  // ========================================================
  const escapeExcel = (value) => {
    if (value === null || value === undefined) {
      return "";
    }
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;");
  };
const scaricaExcel = () => {
  if (!contattiFiltrati || contattiFiltrati.length === 0) {
    alert("Nessun dato da esportare.");
    return;
  }

  const datiExcel = contattiFiltrati.map((contatto) => ({
    "Codice Cliente":
      contatto.codice_cliente ||
      contatto.id_cliente ||
      "",

    "Ragione Sociale":
      contatto.ragione_sociale || "",

    Provincia:
      contatto.provincia || "",

    "Località":
      contatto.localita || "",

    Indirizzo:
      contatto.indirizzo || "",

    CAP:
      contatto.cap || "",

    "P.IVA":
      contatto.par_iva || "",

    Tipo:
      contatto.tipo_contatto || "",

    Nazione:
      contatto.nazione || "",

    Fiera:
      contatto.fiera || "",

    Cliente:
      String(contatto.is_cliente) === "1"
        ? "Sì"
        : "No",

    Fornitore:
      String(contatto.is_fornitore) === "1"
        ? "Sì"
        : "No",

    Meccanica:
      String(contatto.tratta_meccanica) === "1"
        ? "Sì"
        : "No",

    Carrozzeria:
      String(contatto.tratta_carrozzeria) === "1"
        ? "Sì"
        : "No",

    Vetro:
      String(contatto.tratta_vetro) === "1"
        ? "Sì"
        : "No",

    "Note da fare": Number(contatto.note_da_fare || 0),
  }));

  const worksheet =
    XLSX.utils.json_to_sheet(datiExcel);

  worksheet["!cols"] = [
    { wch: 16 }, // Codice Cliente
    { wch: 35 }, // Ragione Sociale
    { wch: 12 }, // Provincia
    { wch: 22 }, // Località
    { wch: 35 }, // Indirizzo
    { wch: 10 }, // CAP
    { wch: 18 }, // P.IVA
    { wch: 18 }, // Tipo
    { wch: 20 }, // Nazione
    { wch: 20 }, // Fiera
    { wch: 12 }, // Cliente
    { wch: 12 }, // Fornitore
    { wch: 12 }, // Meccanica
    { wch: 14 }, // Carrozzeria
    { wch: 10 }, // Vetro
    { wch: 14 }, // Note da fare
  ];

  const workbook =
    XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Contatti"
  );

  const dataOggi = new Date()
    .toISOString()
    .split("T")[0];

  XLSX.writeFile(
    workbook,
    `contatti_filtrati_${dataOggi}.xlsx`
  );
};  // NUOVO
  // ========================================================

  const handleNuovo = () => {
    navigate("/utility/add-contatti");
  };

  const handleApriContatto = (contatto, event) => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }

  const idContatto = Number(contatto?.id_contatto);

  if (!idContatto) {
    console.error("ID contatto non disponibile:", contatto);
    return;
  }

  navigate(
    `/utility/add-contatti?id_contatto=${encodeURIComponent(idContatto)}`,
    {
      state: {
        contactPreview: { ...contatto },
      },
    },
  );
};

  // ========================================================
  // SALVA
  // ========================================================

  const handleSalva = () => {
    console.log("Salva");
  };

  // ========================================================
  // MODIFICA
  // ========================================================

  const handleModifica = () => {
    console.log("Modifica");
  };

  // ========================================================
  // APERTURA BACK ORDER
  // ========================================================

  const handleApriBackOrder = (contatto, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    console.log("=================================");
    console.log("CLICK BACK ORDER");
    console.log("CONTATTO:", contatto);

    const idCliente = contatto?.codice_cliente || contatto?.id_cliente || "";

    const ragioneSociale = contatto?.ragione_sociale || "";

    console.log("ID CLIENTE:", idCliente);
    console.log("RAGIONE SOCIALE:", ragioneSociale);

    if (!idCliente) {
      console.error(
        "ERRORE: il contatto non ha codice_cliente/id_cliente",
        contatto,
      );

      return;
    }

    const destinazione = `/utility/backorder?id_cliente=${encodeURIComponent(
      String(idCliente),
    )}&ragione_sociale=${encodeURIComponent(String(ragioneSociale))}`;

    console.log("NAVIGAZIONE VERSO:", destinazione);

    // React Router
    navigate(destinazione);

    console.log("navigate() eseguito");
  };

  // ========================================================
  // ELIMINA CONTATTO
  // ========================================================

  const apriEliminaContatto = (contatto, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const idContatto = Number(contatto?.id_contatto);

    if (!idContatto) {
      setErrore("Impossibile eliminare il contatto: ID non disponibile.");
      return;
    }

    setContattoDaEliminare(contatto);
    setDeleteModalOpen(true);
  };

  const chiudiEliminaContatto = () => {
    if (deleteBusy) return;

    setDeleteModalOpen(false);
    setContattoDaEliminare(null);
  };

  const confermaEliminaContatto = async () => {
    const idContatto = Number(contattoDaEliminare?.id_contatto);

    if (!idContatto) {
      setErrore("Impossibile eliminare il contatto: ID non disponibile.");
      return;
    }

    try {
      setDeleteBusy(true);
      setErrore("");

      const response = await fetch(DELETE_CONTACT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          action: "delete",
          id_contatto: idContatto,
        }),
      });

      const raw = await response.text();

      let result = {};
      try {
        result = raw ? JSON.parse(raw) : {};
      } catch {
        throw new Error(
          raw
            ? `Risposta PHP non valida: ${raw}`
            : "Il PHP non ha restituito una risposta valida.",
        );
      }

      if (!response.ok || result?.success === false) {
        throw new Error(
          result?.error ||
            result?.message ||
            `Errore HTTP ${response.status}`,
        );
      }

      // Rimuove subito la riga dalla tabella senza dover ricaricare la pagina.
      setContatti((prev) =>
        prev.filter(
          (row) => Number(row?.id_contatto) !== Number(idContatto),
        ),
      );

      setDeleteModalOpen(false);
      setContattoDaEliminare(null);
    } catch (error) {
      console.error("Errore eliminazione contatto:", error);
      setErrore(
        error?.message || "Impossibile eliminare il contatto.",
      );
    } finally {
      setDeleteBusy(false);
    }
  };

  // ========================================================
  // BUTTON COMPONENT
  // ========================================================

  const ActionButton = ({
    children,
    icon: Icon,
    onClick,
    variant = "outline",
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`uw-action-btn ${
        variant === "solid" ? "uw-action-solid" : ""
      }`}
    >
      <Icon size={15} />
      {children}
    </button>
  );

  // ========================================================
  // FILTER CHIP
  // ========================================================

  const FilterChip = ({ label, active, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className={`uw-chip ${active ? "is-active" : ""}`}
    >
      <span className="uw-chip-dot" />

      {label}
    </button>
  );

  // ========================================================
  // STATO DOT
  // ========================================================

  const StatoDot = ({ attivo }) => (
    <span className={`uw-status ${attivo ? "is-active" : ""}`}>
      <span className="uw-status-dot" />
      <span>{attivo ? "Sì" : "No"}</span>
    </span>
  );

  // ========================================================
  // FIERA BADGE
  // ========================================================

  const FieraBadge = ({ nome }) =>
    nome ? (
      <span className="uw-fiera-badge">{nome}</span>
    ) : (
      <span className="uw-muted">-</span>
    );

  // ========================================================
  // INTESTAZIONE ORDINABILE
  // ========================================================

  const SortableTh = ({ sortKey, children, className = "" }) => {
    const active = sortConfig.key === sortKey;

    return (
      <th className={`uw-sort-th ${className}`}>
        <button
          type="button"
          className={`uw-sort-button ${active ? "is-active" : ""}`}
          onClick={() => handleSort(sortKey)}
          title={
            active
              ? sortConfig.direction === "asc"
                ? "Ordine crescente. Clicca per invertire."
                : "Ordine decrescente. Clicca per invertire."
              : "Clicca per ordinare questa colonna."
          }
        >
          <span>{children}</span>

          {active ? (
            sortConfig.direction === "asc" ? (
              <ChevronUp size={14} />
            ) : (
              <ChevronDown size={14} />
            )
          ) : (
            <ArrowUpDown size={13} />
          )}
        </button>
      </th>
    );
  };

  // ========================================================
  // RENDER
  // ========================================================

  return (
    <div
      className="uw-liquid-page"
      style={{
        height: "100vh",
        backgroundColor: BG_PAGE,
        color: TEXT_ON_PAGE,
        width: "100%",
        overflow: "hidden",
      }}
    >
      {/* ==================================================
          NAVBAR
      ================================================== */}

      <NavbarUtility />

      <Container fluid className="uw-page-scroll py-4 px-3 px-md-4">
        {/* ==================================================
            FILTRI
        ================================================== */}

        <Card
          className="uw-card border-0 mb-4 overflow-visible"
          style={{
            backgroundColor: PANEL_BG,
          }}
        >
          <div className="uw-card-accent" />

          <Card.Body className="p-4">
            <div className="d-flex justify-content-between align-items-start gap-4 flex-wrap flex-lg-nowrap">
              {/* ==========================================
                  COLONNA FILTRI
              ========================================== */}

              <div
                className="flex-grow-1"
                style={{
                  minWidth: 0,
                }}
              >
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span
                    style={{
                      width: "16px",
                      height: "2px",
                      backgroundColor: GOLD,
                      display: "inline-block",
                    }}
                  />

                  <h6
                    className="mb-0 fw-bold text-uppercase"
                    style={{
                      color: TEXT_DARK,
                      fontSize: "0.8rem",
                      letterSpacing: "1px",
                    }}
                  >
                    Filtri
                  </h6>

                  {filtriAttivi && (
                    <button
                      type="button"
                      onClick={resetFiltri}
                      className="uw-reset-btn d-flex align-items-center gap-1 ms-2"
                    >
                      <X size={14} />
                      Reset filtri
                    </button>
                  )}
                </div>

                <div className="row g-3">
                  {/* RICERCA */}

                  <div className="col-12 col-xl-3">
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        color: TEXT_DARK,
                        fontSize: "0.85rem",
                      }}
                    >
                      Ricerca
                    </Form.Label>

                    <div className="position-relative">
                      <Search
                        size={17}
                        className="position-absolute"
                        style={{
                          left: "12px",
                          top: "11px",
                          color: TEXT_MUTED,
                        }}
                      />

                      <Form.Control
                        type="text"
                        placeholder="Cerca per ragione sociale..."
                        value={ricerca}
                        onChange={(e) => setRicerca(e.target.value)}
                        className="uw-input"
                        style={{
                          paddingLeft: "40px",
                        }}
                      />
                    </div>
                  </div>

                  {/* FIERA */}

                  <div className="col-12 col-xl-4">
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        color: TEXT_DARK,
                        fontSize: "0.85rem",
                      }}
                    >
                      Fiera
                    </Form.Label>

                    <Form.Select
  value={fiera}
  onChange={(e) => setFiera(e.target.value)}
  className="uw-input"
>
  <option value="">TUTTE LE FIERE</option>

  {fiere.map((nomeFiera) => (
    <option key={nomeFiera} value={nomeFiera}>
      {String(nomeFiera).toUpperCase()}
    </option>
  ))}
</Form.Select>
                  </div>

                  {/* NAZIONE */}

                  <div className="col-12 col-xl-3">
                    <Form.Label
                      className="fw-semibold"
                      style={{
                        color: TEXT_DARK,
                        fontSize: "0.85rem",
                      }}
                    >
                      Nazione
                    </Form.Label>

                    <Form.Select
                      value={nazione}
                      onChange={(e) => setNazione(e.target.value)}
                      className="uw-input"
                    >
                      <option value="">Tutte le nazioni</option>

                      {nazioni.map((nomeNazione) => (
                        <option key={nomeNazione} value={nomeNazione}>
                          {nomeNazione}
                        </option>
                      ))}
                    </Form.Select>
                  </div>

                  {/* RISULTATI */}

                  <div className="col-12 col-xl-2 d-flex align-items-end">
                    <div className="uw-results-box w-100 d-flex align-items-center justify-content-between px-3 py-2">
                      <span
                        style={{
                          fontSize: "0.85rem",
                          color: TEXT_DARK,
                        }}
                      >
                        Risultati
                      </span>

                      <span
                        style={{
                          fontSize: "0.95rem",
                          color: "#e6bd4d",
                          fontWeight: 700,
                        }}
                      >
                        {contattiFiltrati.length}

                        <span
                          style={{
                            color: TEXT_MUTED,
                            fontWeight: 500,
                          }}
                        >
                          {" "}
                          / {contattiUnici.length}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                <hr
                  style={{
                    borderColor: PANEL_BORDER,
                  }}
                />

                <div className="d-flex flex-wrap gap-2">
                  <FilterChip
                    label="Solo clienti"
                    active={soloClienti}
                    onClick={() => setSoloClienti((v) => !v)}
                  />

                  <FilterChip
                    label="Solo fornitori"
                    active={soloFornitori}
                    onClick={() => setSoloFornitori((v) => !v)}
                  />

                  <FilterChip
                    label="Tratta meccanica"
                    active={soloMeccanica}
                    onClick={() => setSoloMeccanica((v) => !v)}
                  />

                  <FilterChip
                    label="Tratta carrozzeria"
                    active={soloCarrozzeria}
                    onClick={() => setSoloCarrozzeria((v) => !v)}
                  />

                  <FilterChip
                    label="Tratta vetro"
                    active={soloVetro}
                    onClick={() => setSoloVetro((v) => !v)}
                  />
                </div>
              </div>

              {/* ==========================================
                  AZIONI
              ========================================== */}

              <div
                className="d-flex flex-column justify-content-center gap-2 flex-shrink-0"
                style={{
                  width: "170px",
                }}
              >
                <ActionButton icon={Plus} onClick={handleNuovo} variant="solid">
                  Nuovo Contatto
                </ActionButton>

                <ActionButton icon={Download} onClick={scaricaExcel}>
                  Scarica Excel
                </ActionButton>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* ==================================================
            LOADING
        ================================================== */}

        {loading && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              minHeight: "300px",
            }}
          >
            <Spinner
              animation="border"
              style={{
                color: GOLD,
              }}
            />
          </div>
        )}

        {/* ==================================================
            ERRORE
        ================================================== */}

        {!loading && errore && (
          <Alert variant="danger" className="uw-glass-alert border-0">
            {errore}
          </Alert>
        )}

        {/* ==================================================
            TABELLA
        ================================================== */}

        {!loading && !errore && (
          <Card
            className="uw-card border-0 overflow-hidden"
            style={{
              backgroundColor: PANEL_BG,
            }}
          >
            <div
              className="scroll-hidden uw-table-shell"
              style={{
                overflowX: "auto",
                maxHeight: "calc(100vh - 380px)",
              }}
            >
              <Table hover responsive className="mb-0 align-middle uw-table">
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    backgroundColor: THEAD_BG,
                    color: THEAD_TEXT,
                  }}
                >
                  <tr>
                    <SortableTh
                      sortKey="codice_cliente"
                      className="uw-sticky-col uw-sticky-col-code"
                    >
                      Codice Cliente
                    </SortableTh>

                    <SortableTh
                      sortKey="ragione_sociale"
                      className="uw-sticky-col uw-sticky-col-name"
                    >
                      Ragione Sociale
                    </SortableTh>

                    <SortableTh sortKey="provincia">Provincia</SortableTh>

                    <SortableTh sortKey="localita">Località</SortableTh>

                    <SortableTh sortKey="indirizzo">Indirizzo</SortableTh>

                    <SortableTh sortKey="cap">CAP</SortableTh>

                    <SortableTh sortKey="par_iva">P.IVA</SortableTh>

                    <SortableTh sortKey="tipo_contatto">Tipo</SortableTh>

                    <SortableTh sortKey="nazione">Nazione</SortableTh>

                    <SortableTh sortKey="fiera">Fiera</SortableTh>

                    <SortableTh sortKey="is_cliente">Cliente</SortableTh>

                    <SortableTh sortKey="is_fornitore">Fornitore</SortableTh>

                    <SortableTh sortKey="tratta_meccanica">
                      Meccanica
                    </SortableTh>

                    <SortableTh sortKey="tratta_carrozzeria">
                      Carrozzeria
                    </SortableTh>

                    <SortableTh sortKey="tratta_vetro">Vetro</SortableTh>

                    <SortableTh sortKey="note_da_fare" className="text-center">
                      Note
                    </SortableTh>

                    <th className="text-end">Azioni</th>
                  </tr>
                </thead>

                <tbody>
                  {contattiFiltrati.length === 0 ? (
                    <tr>
                      <td
                        colSpan="17"
                        className="text-center py-5"
                        style={{
                          color: TEXT_MUTED,
                        }}
                      >
                        Nessun contatto trovato
                      </td>
                    </tr>
                  ) : (
                    contattiFiltrati.map((contatto, index) => (
                      <tr
                        key={
                          contatto.__rowKey ||
                          contatto.id_contatto ||
                          `${contatto.codice_cliente || "no-code"}-${contatto.ragione_sociale || "no-name"}-${index}`
                        }
                      >
                        {/* CODICE CLIENTE */}

                        <td className="uw-sticky-col uw-sticky-col-code">
                          {contatto.codice_cliente ||
                            contatto.id_cliente ||
                            "-"}
                        </td>

                        {/* =================================
                              RAGIONE SOCIALE
                          ================================= */}

                        <td className="uw-sticky-col uw-sticky-col-name">
                          <button
                            type="button"
                            className="ragione-sociale-button"
                            onClick={(event) =>
  handleApriContatto(contatto, event)
}
title="Apri contatto"
                          >
                            <strong>{contatto.ragione_sociale || "-"}</strong>
                          </button>
                        </td>

                        {/* PROVINCIA */}

                        <td>{contatto.provincia || "-"}</td>

                        {/* LOCALITÀ */}

                        <td>{contatto.localita || "-"}</td>

                        {/* INDIRIZZO */}

                        <td>{contatto.indirizzo || "-"}</td>

                        {/* CAP */}

                        <td>{contatto.cap || "-"}</td>

                        {/* P.IVA */}

                        <td>{contatto.par_iva || "-"}</td>

                        {/* TIPO */}

                        <td>{contatto.tipo_contatto || "-"}</td>

                        {/* NAZIONE */}

                        <td>{contatto.nazione || "-"}</td>

                        {/* FIERA */}

                        <td>
                          <FieraBadge nome={contatto.fiera} />
                        </td>

                        {/* CLIENTE */}

                        <td>
                          <StatoDot
                            attivo={String(contatto.is_cliente) === "1"}
                          />
                        </td>

                        {/* FORNITORE */}

                        <td>
                          <StatoDot
                            attivo={String(contatto.is_fornitore) === "1"}
                          />
                        </td>

                        {/* MECCANICA */}

                        <td>
                          <StatoDot
                            attivo={String(contatto.tratta_meccanica) === "1"}
                          />
                        </td>

                        {/* CARROZZERIA */}

                        <td>
                          <StatoDot
                            attivo={String(contatto.tratta_carrozzeria) === "1"}
                          />
                        </td>

                        {/* VETRO */}

                        <td>
                          <StatoDot
                            attivo={String(contatto.tratta_vetro) === "1"}
                          />
                        </td>

                        <td className="text-center">
                          <span
                            className={`uw-note-count ${
                              Number(contatto.note_da_fare || 0) > 0
                                ? "has-notes"
                                : ""
                            }`}
                            title={`${Number(contatto.note_da_fare || 0)} note da fare`}
                          >
                            {Number(contatto.note_da_fare || 0)}
                          </span>
                        </td>

                        <td className="text-end">
                          <button
                            type="button"
                            className="uw-delete-row-btn"
                            onClick={(event) =>
                              apriEliminaContatto(contatto, event)
                            }
                            title={`Elimina ${contatto.ragione_sociale || "contatto"}`}
                            aria-label={`Elimina ${contatto.ragione_sociale || "contatto"}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card>
        )}
      </Container>

      <Modal
        show={deleteModalOpen}
        onHide={chiudiEliminaContatto}
        centered
        backdrop="static"
        keyboard={!deleteBusy}
        dialogClassName="uw-delete-modal-dialog"
        contentClassName="uw-delete-modal"
      >
        <Modal.Header
          closeButton={!deleteBusy}
          className="uw-delete-modal-header"
        >
          <Modal.Title className="uw-delete-modal-title">
            <span className="uw-delete-modal-icon">
              <AlertTriangle size={17} />
            </span>

            Elimina contatto
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="uw-delete-modal-body">
          <div className="uw-delete-modal-warning">
            <strong>
              Vuoi eliminare definitivamente questo contatto?
            </strong>

            <span>
              Verranno eliminate anche le informazioni Utility collegate
              gestite dall'azione di eliminazione.
            </span>
          </div>

          <div className="uw-delete-contact-preview">
            <span>CONTATTO</span>

            <strong>
              {contattoDaEliminare?.ragione_sociale || "—"}
            </strong>

            <small>
              ID {contattoDaEliminare?.id_contatto || "—"}
              {contattoDaEliminare?.codice_cliente
                ? ` · Cliente ${contattoDaEliminare.codice_cliente}`
                : ""}
            </small>
          </div>
        </Modal.Body>

        <Modal.Footer className="uw-delete-modal-footer">
          <button
            type="button"
            className="uw-delete-modal-cancel"
            onClick={chiudiEliminaContatto}
            disabled={deleteBusy}
          >
            <X size={14} />
            Annulla
          </button>

          <button
            type="button"
            className="uw-delete-modal-confirm"
            onClick={confermaEliminaContatto}
            disabled={deleteBusy}
          >
            {deleteBusy ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <Trash2 size={14} />
            )}

            Elimina
          </button>
        </Modal.Footer>
      </Modal>

      {/* ======================================================
          STYLE
      ====================================================== */}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        html,
        body,
        #root {
          width: 100%;
          height: 100%;
          margin: 0;
          background: #121419 !important;
          overflow: hidden;
        }

        body,
        .container-fluid {
          font-family: 'Inter', sans-serif;
        }

        .uw-liquid-page {
          --uw-gold: #ebb51b;
          --uw-gold-dark: #a67a10;
          --uw-white: #f7f7f4;
          --uw-text: #f1f1ee;
          --uw-muted: #a9aaad;
          --uw-muted-2: #81838a;
          --uw-glass-border: rgba(255,255,255,.105);
          --uw-shadow: 0 22px 54px rgba(0,0,0,.38);

          color: var(--uw-text);
          background:
            radial-gradient(circle at 12% 8%, rgba(235,181,27,.10), transparent 28%),
            radial-gradient(circle at 88% 12%, rgba(255,255,255,.045), transparent 27%),
            radial-gradient(circle at 65% 86%, rgba(166,122,16,.055), transparent 34%),
            linear-gradient(145deg, #0d0f13 0%, #1c2027 50%, #0b0d11 100%) !important;
        }

        .uw-liquid-page > nav,
        .uw-liquid-page > .navbar,
        .uw-liquid-page .navbar {
          position: relative;
          z-index: 1000;
          background: rgba(17,20,25,.78) !important;
          backdrop-filter: blur(24px) saturate(145%);
          -webkit-backdrop-filter: blur(24px) saturate(145%);
          border-bottom: 1px solid rgba(235,181,27,.22) !important;
          box-shadow:
            0 12px 34px rgba(0,0,0,.30),
            inset 0 -1px 0 rgba(255,255,255,.035);
          color: var(--uw-white) !important;
        }

        .uw-liquid-page .navbar * {
          color: inherit;
        }

        .uw-liquid-page .uw-page-scroll {
          position: relative;
          height: calc(100vh - 60px);
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          background: transparent;
          padding-bottom: 56px !important;
          scroll-padding-bottom: 56px;
        }

        .uw-card {
          position: relative;
          background:
            radial-gradient(circle at 12% 0%, rgba(255,255,255,.065), transparent 31%),
            linear-gradient(145deg, rgba(58,63,74,.86), rgba(37,41,49,.78)) !important;
          border: 1px solid var(--uw-glass-border) !important;
          border-radius: 24px !important;
          box-shadow:
            var(--uw-shadow),
            inset 0 1px 0 rgba(255,255,255,.075),
            inset 0 -1px 0 rgba(0,0,0,.30) !important;
          backdrop-filter: blur(22px) saturate(140%);
          -webkit-backdrop-filter: blur(22px) saturate(140%);
          color: var(--uw-text);
        }

        .uw-card::after {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          border-radius: inherit;
          background:
            linear-gradient(
              115deg,
              rgba(255,255,255,.042),
              transparent 24%,
              transparent 73%,
              rgba(235,181,27,.024)
            );
        }

        .uw-card > * {
          position: relative;
          z-index: 1;
        }

        .uw-card-accent {
          height: 2px;
          width: 100%;
          background:
            linear-gradient(
              90deg,
              transparent,
              rgba(235,181,27,.38) 15%,
              #ebb51b 50%,
              rgba(166,122,16,.70) 82%,
              transparent
            );
          box-shadow: 0 0 18px rgba(235,181,27,.20);
        }

        .uw-reset-btn {
          background: transparent;
          border: 0;
          color: var(--uw-muted);
          font-size: .78rem;
          font-weight: 600;
          padding: 4px 6px;
          cursor: pointer;
          transition: color .15s ease;
        }

        .uw-reset-btn:hover {
          color: #f0c84d;
        }

        .uw-input,
        .uw-input.form-select {
          min-height: 40px;
          border: 1px solid rgba(255,255,255,.115) !important;
          border-radius: 12px !important;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,.085),
              rgba(255,255,255,.030)
            ) !important;
          color: var(--uw-white) !important;
          -webkit-text-fill-color: var(--uw-white);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.060),
            0 4px 14px rgba(0,0,0,.15);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        .uw-input::placeholder {
          color: #85888f !important;
          -webkit-text-fill-color: #85888f;
          opacity: 1;
        }

        .uw-input:focus {
          border-color: rgba(235,181,27,.68) !important;
          box-shadow:
            0 0 0 3px rgba(235,181,27,.11),
            0 8px 22px rgba(0,0,0,.20) !important;
          outline: none !important;
        }

        select.uw-input option {
          background: #292d35;
          color: #f4f4f1;
        }

        .uw-results-box {
          min-height: 40px;
          border: 1px solid rgba(235,181,27,.26);
          border-radius: 12px;
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,.11),
              rgba(255,255,255,.035)
            );
          box-shadow: inset 0 1px 0 rgba(255,255,255,.045);
        }

        .uw-chip {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          border: 1px solid rgba(255,255,255,.105);
          border-radius: 999px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,.055),
              rgba(255,255,255,.018)
            );
          color: var(--uw-muted);
          font-size: .78rem;
          font-weight: 600;
          padding: 7px 13px;
          cursor: pointer;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.035);
          transition: all .16s ease;
        }

        .uw-chip:hover {
          border-color: rgba(235,181,27,.35);
          color: #e3e3df;
        }

        .uw-chip.is-active {
          border-color: rgba(235,181,27,.62);
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,.18),
              rgba(235,181,27,.065)
            );
          color: #f0cb60;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.055),
            0 6px 16px rgba(0,0,0,.14);
        }

        .uw-chip-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          display: inline-block;
          background: #6f7279;
          box-shadow: 0 0 0 3px rgba(255,255,255,.025);
        }

        .uw-chip.is-active .uw-chip-dot {
          background: #ebb51b;
          box-shadow: 0 0 10px rgba(235,181,27,.42);
        }

        .uw-action-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          border: 1px solid rgba(235,181,27,.38);
          border-radius: 12px;
          background:
            linear-gradient(
              145deg,
              rgba(255,255,255,.075),
              rgba(255,255,255,.022)
            );
          color: #e6bd4d;
          font-size: .80rem;
          font-weight: 700;
          padding: 9px 12px;
          cursor: pointer;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.050),
            0 7px 18px rgba(0,0,0,.16);
          transition: all .16s ease;
        }

        .uw-action-btn:hover {
          transform: translateY(-1px);
          border-color: rgba(235,181,27,.62);
          background:
            linear-gradient(
              145deg,
              rgba(235,181,27,.15),
              rgba(255,255,255,.050)
            );
          color: #fff;
        }

        .uw-action-solid {
          background:
            linear-gradient(145deg, #f0c43e, #a67a10) !important;
          color: #17191e !important;
          border-color: rgba(235,181,27,.75) !important;
          box-shadow:
            0 9px 22px rgba(0,0,0,.23),
            inset 0 1px 0 rgba(255,255,255,.28);
        }

        .uw-action-solid:hover {
          background:
            linear-gradient(145deg, #f5cf57, #b88812) !important;
          color: #121419 !important;
        }

        .uw-table-shell {
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 20px;
          overflow: auto !important;
          box-shadow:
            0 14px 34px rgba(0,0,0,.23),
            inset 0 1px 0 rgba(255,255,255,.040);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
        }

        .uw-table {
          --bs-table-bg: transparent;
          --bs-table-color: var(--uw-text);
          --bs-table-hover-bg: transparent;
          margin: 0;
          background: transparent !important;
        }

        .uw-table thead th {
          position: sticky;
          top: 0;
          z-index: 4;
          background: rgba(32,36,43,.96) !important;
          color: #d7b84f !important;
          border-color: rgba(255,255,255,.075) !important;
          font-size: .70rem;
          text-transform: uppercase;
          letter-spacing: .55px;
          font-weight: 700;
          padding: 12px 14px;
          white-space: nowrap;
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
        }

        .uw-sort-th {
          padding: 0 !important;
        }

        .uw-sort-button {
          width: 100%;
          min-height: 42px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 12px 14px;
          border: 0;
          background: transparent;
          color: inherit;
          font: inherit;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .55px;
          white-space: nowrap;
          cursor: pointer;
          transition:
            color .15s ease,
            background .15s ease;
        }

        .uw-sort-button svg {
          flex-shrink: 0;
          color: rgba(215,184,79,.55);
          transition:
            color .15s ease,
            transform .15s ease;
        }

        .uw-sort-button:hover {
          color: #f3ce61;
          background: rgba(235,181,27,.055);
        }

        .uw-sort-button:hover svg,
        .uw-sort-button.is-active svg {
          color: #ebb51b;
        }

        .uw-sort-button.is-active {
          color: #f0c84d;
          background: rgba(235,181,27,.075);
        }

        .uw-sort-button:focus-visible {
          outline: 2px solid rgba(235,181,27,.75);
          outline-offset: -3px;
        }

        .uw-sort-th.text-center .uw-sort-button {
          justify-content: center;
        }

        .uw-table tbody td {
          font-size: .82rem;
          padding: 10px 14px;
          color: #ecece8 !important;
          border-color: rgba(255,255,255,.055) !important;
          background: rgba(255,255,255,.030) !important;
        }

        .uw-table tbody tr:nth-child(even) td {
          background: rgba(255,255,255,.043) !important;
        }

        .uw-table tbody tr:hover td {
          background: rgba(235,181,27,.072) !important;
        }

        /* ======================================================
           PRIME 2 COLONNE FISSE DURANTE LO SCROLL ORIZZONTALE
        ====================================================== */

        .uw-table {
          --uw-code-col-width: 132px;
          --uw-name-col-width: 280px;
        }

        .uw-table .uw-sticky-col-code {
          position: sticky !important;
          left: 0;
          width: var(--uw-code-col-width);
          min-width: var(--uw-code-col-width);
          max-width: var(--uw-code-col-width);
        }

        .uw-table .uw-sticky-col-name {
          position: sticky !important;
          left: var(--uw-code-col-width);
          width: var(--uw-name-col-width);
          min-width: var(--uw-name-col-width);
          max-width: var(--uw-name-col-width);
        }

        /* Header: deve stare sopra sia alle righe sia alle altre intestazioni. */
        .uw-table thead .uw-sticky-col {
          z-index: 8 !important;
          background: rgba(32,36,43,.99) !important;
          background-clip: padding-box !important;
        }

        /* Celle fisse del body. */
        .uw-table tbody .uw-sticky-col {
          z-index: 3;
          background: rgba(37,41,49,.99) !important;
          background-clip: padding-box !important;
        }

        .uw-table tbody tr:nth-child(even) .uw-sticky-col {
          background: rgba(42,46,55,.99) !important;
        }

        .uw-table tbody tr:hover .uw-sticky-col {
          background: rgb(55,52,43) !important;
        }

        /* Linea di separazione dopo Ragione Sociale. */
        .uw-table .uw-sticky-col-name {
          border-right: 1px solid rgba(235,181,27,.24) !important;
        }

        /* Evita che il nome deformi le colonne bloccate. */
        .uw-table .uw-sticky-col-name,
        .uw-table .uw-sticky-col-name .uw-sort-button,
        .uw-table .uw-sticky-col-name .ragione-sociale-button {
          overflow: hidden;
        }

        .uw-table .uw-sticky-col-name .ragione-sociale-button strong {
          display: block;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        @media (max-width: 700px) {
          .uw-table {
            --uw-code-col-width: 112px;
            --uw-name-col-width: 220px;
          }
        }

        .ragione-sociale-button {
          border: 0;
          background: transparent;
          padding: 0;
          margin: 0;
          color: var(--uw-white);
          cursor: pointer;
          text-align: left;
          font-family: inherit;
          font-size: inherit;
          width: 100%;
        }

        .ragione-sociale-button strong {
          color: #f5f5f2 !important;
          transition: color .12s ease;
        }

        .ragione-sociale-button:hover strong {
          color: #efc95b !important;
          text-decoration: underline;
        }

        .ragione-sociale-button:focus {
          outline: none;
        }

        .ragione-sociale-button:focus-visible {
          outline: 2px solid var(--uw-gold);
          outline-offset: 3px;
          border-radius: 4px;
        }

        .uw-status {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          color: #777a82;
          font-weight: 500;
        }

        .uw-status-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          flex-shrink: 0;
          background: #62656d;
        }

        .uw-status.is-active {
          color: #ecece8;
          font-weight: 650;
        }

        .uw-status.is-active .uw-status-dot {
          background: #47bd73;
          box-shadow: 0 0 10px rgba(71,189,115,.32);
        }

        .uw-fiera-badge {
          display: inline-block;
          border: 1px solid rgba(235,181,27,.42);
          border-radius: 999px;
          background: rgba(235,181,27,.075);
          color: #e8c65d;
          font-size: .70rem;
          font-weight: 650;
          padding: 3px 9px;
          white-space: nowrap;
        }

        .uw-muted {
          color: var(--uw-muted);
        }

        .uw-glass-alert {
          border: 1px solid rgba(211,81,81,.28) !important;
          border-left: 3px solid rgba(211,81,81,.78) !important;
          border-radius: 16px !important;
          background:
            linear-gradient(
              145deg,
              rgba(150,45,45,.20),
              rgba(255,255,255,.030)
            ) !important;
          color: #f0c4c4 !important;
          box-shadow:
            0 12px 28px rgba(0,0,0,.20),
            inset 0 1px 0 rgba(255,255,255,.045);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
        }

        .scroll-hidden {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .scroll-hidden::-webkit-scrollbar {
          display: none;
        }

        @media (max-width: 991.98px) {
          .uw-card {
            border-radius: 18px !important;
          }

          .uw-page-scroll {
            height: calc(100vh - 56px) !important;
          }
        }

        select.uw-input {
  text-transform: uppercase;
}

select.uw-input option {
  text-transform: uppercase;
}


.uw-note-count {
  min-width: 27px;
  height: 27px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 0 7px;

  border-radius: 999px;

  color: #7f838b;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);

  font-size: 11px;
  font-weight: 800;

  transition: all 0.15s ease;
}

.uw-note-count.has-notes {
  color: #ffd45a;

  background: rgba(235, 181, 27, 0.13);

  border-color: rgba(235, 181, 27, 0.46);

  box-shadow:
    0 0 12px rgba(235, 181, 27, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.07);
}

        .uw-delete-row-btn {
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border-radius: 10px;
          border: 1px solid rgba(255, 86, 86, .18);
          background:
            linear-gradient(
              145deg,
              rgba(255, 74, 74, .085),
              rgba(255, 255, 255, .022)
            );
          color: #d97d7d;
          cursor: pointer;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.04),
            0 5px 14px rgba(0,0,0,.12);
          transition:
            transform .15s ease,
            border-color .15s ease,
            color .15s ease,
            background .15s ease;
        }

        .uw-delete-row-btn:hover {
          transform: translateY(-1px);
          color: #ffb0b0;
          border-color: rgba(255, 86, 86, .42);
          background:
            linear-gradient(
              145deg,
              rgba(255, 74, 74, .16),
              rgba(255,255,255,.038)
            );
        }

        .modal-dialog.uw-delete-modal-dialog {
          width: min(92vw, 460px) !important;
          max-width: 460px !important;
          margin-left: auto !important;
          margin-right: auto !important;
        }

        .uw-delete-modal {
          position: relative;
          overflow: hidden;
          color: #f5f5f2 !important;
          border: 1px solid rgba(255,255,255,.13) !important;
          border-radius: 22px !important;
          background:
            radial-gradient(circle at 8% 0%, rgba(185,55,55,.15), transparent 34%),
            linear-gradient(
              145deg,
              rgba(48,52,61,.93),
              rgba(27,30,37,.90)
            ) !important;
          backdrop-filter: blur(30px) saturate(155%);
          -webkit-backdrop-filter: blur(30px) saturate(155%);
          box-shadow:
            0 28px 80px rgba(0,0,0,.50),
            inset 0 1px 0 rgba(255,255,255,.10) !important;
        }

        .uw-delete-modal-header,
        .uw-delete-modal-footer {
          border: 0 !important;
          background: transparent !important;
        }

        .uw-delete-modal-header {
          padding: 15px 16px !important;
          border-bottom: 1px solid rgba(255,255,255,.075) !important;
        }

        .uw-delete-modal-title {
          display: flex;
          align-items: center;
          gap: 9px;
          color: #f4f4f1 !important;
          font-size: .88rem !important;
          font-weight: 800 !important;
          letter-spacing: .45px;
          text-transform: uppercase;
        }

        .uw-delete-modal-icon {
          width: 31px;
          height: 31px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          color: #ff9292;
          background: rgba(255,74,74,.10);
          border: 1px solid rgba(255,90,90,.19);
        }

        .uw-delete-modal .btn-close {
          width: 30px;
          height: 30px;
          padding: 0 !important;
          margin: 0 !important;
          border-radius: 50%;
          opacity: .80;
          filter: invert(1) grayscale(1) brightness(1.7);
          background-size: 10px;
          background-color: rgba(255,255,255,.05);
        }

        .uw-delete-modal-body {
          padding: 16px !important;
        }

        .uw-delete-modal-warning {
          display: flex;
          flex-direction: column;
          gap: 5px;
          padding: 12px 13px;
          border-radius: 13px;
          border: 1px solid rgba(255,88,88,.12);
          background: rgba(255,66,66,.055);
        }

        .uw-delete-modal-warning strong {
          color: #f5f5f2;
          font-size: .82rem;
        }

        .uw-delete-modal-warning span {
          color: #a9aaad;
          font-size: .74rem;
          line-height: 1.4;
        }

        .uw-delete-contact-preview {
          display: flex;
          flex-direction: column;
          gap: 3px;
          margin-top: 12px;
          padding: 12px 13px;
          border-radius: 13px;
          border: 1px solid rgba(255,255,255,.08);
          background: rgba(255,255,255,.035);
        }

        .uw-delete-contact-preview span {
          color: #7f838b;
          font-size: .62rem;
          font-weight: 800;
          letter-spacing: .55px;
        }

        .uw-delete-contact-preview strong {
          color: #f3f3ef;
          font-size: .90rem;
        }

        .uw-delete-contact-preview small {
          color: #92959c;
          font-size: .70rem;
        }

        .uw-delete-modal-footer {
          gap: 8px;
          padding: 12px 16px 15px !important;
          border-top: 1px solid rgba(255,255,255,.065) !important;
        }

        .uw-delete-modal-cancel,
        .uw-delete-modal-confirm {
          min-height: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          padding: 7px 13px;
          border-radius: 11px;
          font-size: .75rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .3px;
          cursor: pointer;
          transition: all .15s ease;
        }

        .uw-delete-modal-cancel {
          color: #d1d3d7;
          border: 1px solid rgba(255,255,255,.10);
          background: rgba(255,255,255,.045);
        }

        .uw-delete-modal-cancel:hover:not(:disabled) {
          color: #fff;
          background: rgba(255,255,255,.085);
        }

        .uw-delete-modal-confirm {
          color: #fff;
          border: 1px solid rgba(255,94,94,.36);
          background:
            linear-gradient(
              145deg,
              #c94c4c,
              #8e2e2e
            );
          box-shadow:
            0 8px 20px rgba(100,15,15,.24),
            inset 0 1px 0 rgba(255,255,255,.12);
        }

        .uw-delete-modal-confirm:hover:not(:disabled) {
          transform: translateY(-1px);
          background:
            linear-gradient(
              145deg,
              #dc5a5a,
              #a23737
            );
        }

        .uw-delete-modal-cancel:disabled,
        .uw-delete-modal-confirm:disabled {
          opacity: .48;
          cursor: not-allowed;
        }

        @media (max-width: 576px) {
          .modal-dialog.uw-delete-modal-dialog {
            width: calc(100% - 22px) !important;
            max-width: none !important;
          }

          .uw-delete-modal-footer {
            display: grid !important;
            grid-template-columns: 1fr 1fr;
          }
        }

      `}</style>
    </div>
  );
};

export default Contatti;